
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SalaryCalculator: React.FC = () => {
  const [calculationType, setCalculationType] = useState<string>("annual-to-hourly");
  
  // Annual to hourly conversion
  const [annualSalary, setAnnualSalary] = useState<number>(50000);
  const [hoursPerWeek, setHoursPerWeek] = useState<number>(40);
  const [weeksPerYear, setWeeksPerYear] = useState<number>(52);
  const [hourlyRate, setHourlyRate] = useState<number>(0);
  
  // Hourly to annual conversion
  const [hourlyWage, setHourlyWage] = useState<number>(24);
  const [hoursPerWeekReverse, setHoursPerWeekReverse] = useState<number>(40);
  const [weeksPerYearReverse, setWeeksPerYearReverse] = useState<number>(52);
  const [annualIncome, setAnnualIncome] = useState<number>(0);
  
  // Salary comparison
  const [currentSalary, setCurrentSalary] = useState<number>(50000);
  const [newSalary, setNewSalary] = useState<number>(60000);
  const [percentIncrease, setPercentIncrease] = useState<number>(0);
  const [dollarIncrease, setDollarIncrease] = useState<number>(0);

  const calculateAnnualToHourly = () => {
    try {
      // Validation
      if (annualSalary < 0 || hoursPerWeek <= 0 || weeksPerYear <= 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for all fields",
          variant: "destructive"
        });
        return;
      }

      const totalHours = hoursPerWeek * weeksPerYear;
      const hourlyRateValue = annualSalary / totalHours;
      
      setHourlyRate(hourlyRateValue);

      toast({
        title: "Calculation complete",
        description: "Your hourly rate has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const calculateHourlyToAnnual = () => {
    try {
      // Validation
      if (hourlyWage < 0 || hoursPerWeekReverse <= 0 || weeksPerYearReverse <= 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for all fields",
          variant: "destructive"
        });
        return;
      }

      const totalHours = hoursPerWeekReverse * weeksPerYearReverse;
      const annualIncomeValue = hourlyWage * totalHours;
      
      setAnnualIncome(annualIncomeValue);

      toast({
        title: "Calculation complete",
        description: "Your annual income has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const calculateSalaryComparison = () => {
    try {
      // Validation
      if (currentSalary <= 0 || newSalary <= 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid positive numbers for both salaries",
          variant: "destructive"
        });
        return;
      }

      const dollarIncreaseValue = newSalary - currentSalary;
      const percentIncreaseValue = (dollarIncreaseValue / currentSalary) * 100;
      
      setDollarIncrease(dollarIncreaseValue);
      setPercentIncrease(percentIncreaseValue);

      toast({
        title: "Calculation complete",
        description: "Your salary comparison has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <Tabs 
        value={calculationType} 
        onValueChange={setCalculationType} 
        className="space-y-4"
      >
        <TabsList className="grid grid-cols-3">
          <TabsTrigger value="annual-to-hourly">Annual to Hourly</TabsTrigger>
          <TabsTrigger value="hourly-to-annual">Hourly to Annual</TabsTrigger>
          <TabsTrigger value="salary-comparison">Salary Comparison</TabsTrigger>
        </TabsList>
        
        {/* Annual to Hourly */}
        <TabsContent value="annual-to-hourly" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="annualSalary">Annual Salary ($)</Label>
                <Input
                  id="annualSalary"
                  type="number"
                  value={annualSalary}
                  onChange={(e) => setAnnualSalary(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hoursPerWeek">Hours Worked Per Week</Label>
                <Input
                  id="hoursPerWeek"
                  type="number"
                  value={hoursPerWeek}
                  onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="weeksPerYear">Weeks Worked Per Year</Label>
                <Input
                  id="weeksPerYear"
                  type="number"
                  value={weeksPerYear}
                  onChange={(e) => setWeeksPerYear(Number(e.target.value))}
                />
              </div>

              <Button onClick={calculateAnnualToHourly} className="mt-4 w-full">Calculate Hourly Rate</Button>
            </div>

            <div>
              <Card className="border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Hourly Rate</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Annual Salary</div>
                      <div className="text-lg font-medium">{formatCurrency(annualSalary)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Hours Worked Per Year</div>
                      <div className="text-lg font-medium">{hoursPerWeek * weeksPerYear} hours</div>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Hourly Rate</div>
                      <div className="text-xl font-bold text-primary">
                        {hourlyRate ? formatCurrency(hourlyRate) + "/hour" : '-'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Hourly to Annual */}
        <TabsContent value="hourly-to-annual" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hourlyWage">Hourly Wage ($)</Label>
                <Input
                  id="hourlyWage"
                  type="number"
                  value={hourlyWage}
                  onChange={(e) => setHourlyWage(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hoursPerWeekReverse">Hours Worked Per Week</Label>
                <Input
                  id="hoursPerWeekReverse"
                  type="number"
                  value={hoursPerWeekReverse}
                  onChange={(e) => setHoursPerWeekReverse(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="weeksPerYearReverse">Weeks Worked Per Year</Label>
                <Input
                  id="weeksPerYearReverse"
                  type="number"
                  value={weeksPerYearReverse}
                  onChange={(e) => setWeeksPerYearReverse(Number(e.target.value))}
                />
              </div>

              <Button onClick={calculateHourlyToAnnual} className="mt-4 w-full">Calculate Annual Income</Button>
            </div>

            <div>
              <Card className="border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Annual Income</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Hourly Wage</div>
                      <div className="text-lg font-medium">{formatCurrency(hourlyWage)}/hour</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Hours Worked Per Year</div>
                      <div className="text-lg font-medium">{hoursPerWeekReverse * weeksPerYearReverse} hours</div>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Annual Income</div>
                      <div className="text-xl font-bold text-primary">
                        {annualIncome ? formatCurrency(annualIncome) + "/year" : '-'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {annualIncome ? `(${formatCurrency(annualIncome / 12)}/month)` : ''}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Salary Comparison */}
        <TabsContent value="salary-comparison" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentSalary">Current Salary ($)</Label>
                <Input
                  id="currentSalary"
                  type="number"
                  value={currentSalary}
                  onChange={(e) => setCurrentSalary(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newSalary">New Salary ($)</Label>
                <Input
                  id="newSalary"
                  type="number"
                  value={newSalary}
                  onChange={(e) => setNewSalary(Number(e.target.value))}
                />
              </div>

              <Button onClick={calculateSalaryComparison} className="mt-4 w-full">Compare Salaries</Button>
            </div>

            <div>
              <Card className="border-2 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Salary Comparison</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Current Salary</div>
                        <div className="text-base font-medium">{formatCurrency(currentSalary)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">New Salary</div>
                        <div className="text-base font-medium">{formatCurrency(newSalary)}</div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Dollar Difference</div>
                      <div className="text-lg font-medium">
                        {dollarIncrease ? formatCurrency(dollarIncrease) : '-'}
                        <span className="text-sm text-muted-foreground ml-2">
                          {dollarIncrease ? `(${formatCurrency(dollarIncrease / 12)}/month)` : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-muted-foreground">Percentage Change</div>
                      <div className="text-xl font-bold text-primary">
                        {percentIncrease ? `${percentIncrease > 0 ? '+' : ''}${percentIncrease.toFixed(2)}%` : '-'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalaryCalculator;
